/**
 * @file TransportEmailNotices.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description The planning boards' header notices — one component for the
 *              bulk board (TransportPlanning) and the Haulage/Bag board
 *              (HaulageJobsPlanning), which each had their own copy.
 *
 *   Cam, 30 Oct 2026: "can we make sure if an email bounces or fails to
 *   delivery for any reason, a warning triangle icon shows in transport
 *   planning header by the paperwork emailed slideouts please. Can those
 *   slideouts and the warning icon all be expanded rather than slideout
 *   please, and nice and clear. Keep them as narrow as you can (some stacking
 *   of contents I suggest) and no taller than the header is now."
 *
 *   - Mills emailed / Paperwork emailed / Office emailed are ALWAYS OPEN: the
 *     icon, then the words stacked — what, when, who (on a narrow header the
 *     notices sit two to a column and "who" moves to the tooltip, so the
 *     header is never taller than it was). Mills and paperwork open the send
 *     history (PaperworkHistoryModal) in one click; the office summary has no
 *     history of its own, as before.
 *   - Beside them, whenever any of the day's driver paperwork, mill or office
 *     emails bounced, was marked as spam, was rejected or failed to send
 *     (hooks/useTransportEmailProblems, rule utils/emailDelivery): a red
 *     triangle, "1 email bounced" over which emails, opening Plan History on
 *     its Emails tab. Its tooltip names every address it did not reach, and why.
 *   - HeaderNotice — the circle that slides out on a click — stays for
 *     "Last auto-saved" only; it lives here so the two boards share it.
 */

import React, { useEffect, useRef, useState } from 'react';
import useTransportEmailProblems from '../hooks/useTransportEmailProblems';
import { deliveryStatus, problemsHeadline, problemsKinds } from '../utils/emailDelivery';
import './TransportEmailNotices.css';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "30 Oct 14:32", in London time. */
export function shortWhen(at) {
  const d = new Date(at);
  if (at == null || Number.isNaN(d.getTime())) return '';
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).formatToParts(d).map(p => [p.type, p.value]));
  return `${parts.day} ${MONTHS[Number(parts.month) - 1]} ${parts.hour}:${parts.minute}`;
}

/** "Friday 30 October 2026 at 14:32", in London time — for the tooltips. */
function longWhen(at) {
  const d = new Date(at);
  if (at == null || Number.isNaN(d.getTime())) return '';
  const day = d.toLocaleDateString('en-GB', { timeZone: 'Europe/London', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const time = d.toLocaleTimeString('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit' });
  return `${day} at ${time}`;
}

/**
 * The warning's tooltip: each address that did not get an email, and why.
 *   "Mill order summary (30 Oct 14:32) — kelly.berr@… bounced: smtp; 550 …"
 */
export function problemsTitle(problems) {
  const lines = [];
  for (const p of problems || []) {
    const who = (p.recipients && p.recipients.length)
      ? p.recipients.map(r => `${r.address} ${deliveryStatus(r.status).verb || deliveryStatus(r.status).label.toLowerCase()}${r.reason ? `: ${r.reason}` : ''}`)
      : [`${(p.to || []).join(', ') || 'no address'} ${deliveryStatus(p.status).verb || deliveryStatus(p.status).label.toLowerCase()}${p.error ? `: ${p.error}` : ''}`];
    lines.push(`${p.label || 'Email'} (${shortWhen(p.at)}) — ${who.join('; ')}`);
  }
  lines.push('Click to see every email for this day in Plan History.');
  return lines.join('\n');
}

/*
 * Header status pill: a bordered circle showing just an icon, expanding on
 * click to reveal its detail. It auto-collapses 2s after the cursor leaves,
 * staying open while hovered. Now "Last auto-saved" only.
 */
export function HeaderNotice({ variant, icon, children, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const timerRef = useRef(null);
  const clear = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };
  useEffect(() => clear, []);
  return (
    <div
      className={`header-notice ${variant} ${expanded ? 'expanded' : ''}`}
      role="button"
      tabIndex={0}
      // Expanded already and it has a history: the second click opens it.
      onClick={() => { clear(); if (expanded && onOpen) onOpen(); else setExpanded(true); }}
      title={expanded && onOpen ? 'Click to see everything sent for this day' : undefined}
      onMouseEnter={clear}
      onMouseLeave={() => { if (expanded) { clear(); timerRef.current = setTimeout(() => setExpanded(false), 2000); } }}
    >
      <span className="material-symbols-rounded header-notice-icon">{icon}</span>
      <span className="header-notice-body">{children}</span>
    </div>
  );
}

/* One always-open notice: icon, then what / when / who stacked. */
function EmailNotice({ variant, icon, what, when, who, title, onClick }) {
  const body = (
    <>
      <span className="material-symbols-rounded tpen-icon" aria-hidden="true">{icon}</span>
      <span className="tpen-text">
        <span className="tpen-what">{what}</span>
        {when && <span className="tpen-when">{when}</span>}
        {who && <span className="tpen-who">{who}</span>}
      </span>
    </>
  );
  return onClick
    ? <button type="button" className={`tpen-notice ${variant}`} title={title} onClick={onClick}>{body}</button>
    : <div className={`tpen-notice ${variant}`} title={title}>{body}</div>;
}

/**
 * @param {string} date - the delivery day on the board ('YYYY-MM-DD')
 * @param {object} supplierLog / paperworkLog / officeLog - the latest send of
 *        each kind ({ sent_at, sent_by_name }), or null when none was sent
 * @param {function} onOpenSends - (kind: 'supplier' | 'paperwork') → the send history
 * @param {function} onOpenPlanHistory - (tab) → Plan History on that tab
 * @param {boolean} canSeeHistory - Plan History is not a rep's: no problems are read for them
 */
function TransportEmailNotices({
  date, supplierLog, paperworkLog, officeLog, onOpenSends, onOpenPlanHistory, canSeeHistory = true
}) {
  // A new send of any kind reads the problems again.
  const refreshKey = [supplierLog?.sent_at, paperworkLog?.sent_at, officeLog?.sent_at].map(v => v || '').join('|');
  const { problems } = useTransportEmailProblems(date, { enabled: canSeeHistory, refreshKey });

  if (!supplierLog && !paperworkLog && !officeLog && !problems.length) return null;

  const sentTitle = (what, log, more) => [
    `${what} ${longWhen(log.sent_at)}${log.sent_by_name ? ` by ${log.sent_by_name}` : ''}`,
    more
  ].filter(Boolean).join('\n');

  return (
    <div className="tp-email-notices" aria-label="Emails sent for this day">
      {supplierLog && (
        <EmailNotice
          variant="tpen-supplier"
          icon="forward_to_inbox"
          what="Mills emailed"
          when={shortWhen(supplierLog.sent_at)}
          who={supplierLog.sent_by_name || null}
          title={sentTitle('Order summaries emailed to the mills', supplierLog, 'Click to see every mill email sent for this day.')}
          onClick={onOpenSends ? () => onOpenSends('supplier') : undefined}
        />
      )}
      {paperworkLog && (
        <EmailNotice
          variant="tpen-paperwork"
          icon="mark_email_read"
          what="Paperwork emailed"
          when={shortWhen(paperworkLog.sent_at)}
          who={paperworkLog.sent_by_name || null}
          title={sentTitle('Driver paperwork emailed', paperworkLog, 'Click to see every driver paperwork email sent for this day.')}
          onClick={onOpenSends ? () => onOpenSends('paperwork') : undefined}
        />
      )}
      {officeLog && (
        <EmailNotice
          variant="tpen-office"
          icon="summarize"
          what="Office emailed"
          when={shortWhen(officeLog.sent_at)}
          who={officeLog.sent_by_name || null}
          title={sentTitle('Office summary emailed', officeLog)}
        />
      )}
      {problems.length > 0 && (
        <EmailNotice
          variant="tpen-warning"
          icon="warning"
          what={problemsHeadline(problems)}
          when={problemsKinds(problems)}
          who="See Plan History"
          title={problemsTitle(problems)}
          onClick={onOpenPlanHistory ? () => onOpenPlanHistory('email') : undefined}
        />
      )}
    </div>
  );
}

export default TransportEmailNotices;
